<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCountries } from '@/features/merchants/queries/useCountries'
import { useCreateOrganization } from '@/features/organizations/mutations/useCreateOrganization'
import {
  EMPTY_ORGANIZATION_FORM,
  organizationFormSchema,
  toOrganizationFieldErrors,
} from '@/features/organizations/utils/organizationForm'

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const createOrganization = useCreateOrganization()
const { data: countries } = useCountries()

// The backend only accepts ISO 3166-1 alpha-2 codes, so the country is picked, not typed.
const countryOptions = computed(() =>
  (countries.value ?? []).map(({ code, name }) => ({ value: code, label: name })),
)

const schema = computed(() => toTypedSchema(organizationFormSchema(t)))

const { defineField, handleSubmit, errors, setErrors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: EMPTY_ORGANIZATION_FORM,
})

const [name, nameAttrs] = defineField('name')
const [companyName, companyNameAttrs] = defineField('companyName')
const [email, emailAttrs] = defineField('email')
const [vatNumber, vatNumberAttrs] = defineField('vatNumber')
const [streetAddress, streetAddressAttrs] = defineField('streetAddress')
const [zipCode, zipCodeAttrs] = defineField('zipCode')
const [city, cityAttrs] = defineField('city')
const [country] = defineField('country')

const onSubmit = handleSubmit((values) => {
  createOrganization.mutate(values, {
    onSuccess: () => {
      open.value = false
      resetForm()
    },
    onError: (error: unknown) => setErrors(toOrganizationFieldErrors(error)),
  })
})

function handleOpenChange(value: boolean): void {
  if (!value) resetForm()
}
</script>

<template>
  <Dialog v-model:open="open" @update:open="handleOpenChange">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ t('organizations.createDialog.title') }}</DialogTitle>
        <DialogDescription>{{ t('organizations.createDialog.description') }}</DialogDescription>
      </DialogHeader>

      <form id="create-organization-form" class="space-y-4" novalidate @submit.prevent="onSubmit">
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1.5">
            <label for="organization-name" class="text-sm font-medium">{{
              t('organizations.createDialog.name')
            }}</label>
            <Input
              id="organization-name"
              v-model="name"
              v-bind="nameAttrs"
              :placeholder="t('organizations.createDialog.namePlaceholder')"
            />
            <p v-if="errors.name" class="text-destructive text-xs">{{ errors.name }}</p>
          </div>
          <div class="space-y-1.5">
            <label for="organization-company-name" class="text-sm font-medium">{{
              t('organizations.createDialog.companyName')
            }}</label>
            <Input
              id="organization-company-name"
              v-model="companyName"
              v-bind="companyNameAttrs"
              :placeholder="t('organizations.createDialog.companyNamePlaceholder')"
            />
            <p v-if="errors.companyName" class="text-destructive text-xs">
              {{ errors.companyName }}
            </p>
          </div>
        </div>

        <h3 class="text-muted-foreground pt-1 text-[11px] font-medium tracking-wide uppercase">
          {{ t('organizations.createDialog.optionalDetails') }}
        </h3>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1.5">
            <label for="organization-email" class="text-sm font-medium">
              {{ t('organizations.createDialog.email') }}
            </label>
            <Input
              id="organization-email"
              v-model="email"
              v-bind="emailAttrs"
              type="email"
              :placeholder="t('organizations.createDialog.emailPlaceholder')"
            />
            <p v-if="errors.email" class="text-destructive text-xs">{{ errors.email }}</p>
          </div>
          <div class="space-y-1.5">
            <label for="organization-vat-number" class="text-sm font-medium">
              {{ t('organizations.createDialog.vatNumber') }}
            </label>
            <Input
              id="organization-vat-number"
              v-model="vatNumber"
              v-bind="vatNumberAttrs"
              :placeholder="t('organizations.createDialog.vatNumberPlaceholder')"
            />
            <p v-if="errors.vatNumber" class="text-destructive text-xs">{{ errors.vatNumber }}</p>
          </div>
        </div>

        <div class="space-y-1.5">
          <label for="organization-street-address" class="text-sm font-medium">
            {{ t('organizations.createDialog.streetAddress') }}
          </label>
          <Input
            id="organization-street-address"
            v-model="streetAddress"
            v-bind="streetAddressAttrs"
            autocomplete="street-address"
          />
          <p v-if="errors.streetAddress" class="text-destructive text-xs">
            {{ errors.streetAddress }}
          </p>
        </div>

        <div class="grid gap-4 sm:grid-cols-3">
          <div class="space-y-1.5">
            <label for="organization-zip-code" class="text-sm font-medium">{{
              t('organizations.createDialog.zipCode')
            }}</label>
            <Input
              id="organization-zip-code"
              v-model="zipCode"
              v-bind="zipCodeAttrs"
              autocomplete="postal-code"
            />
            <p v-if="errors.zipCode" class="text-destructive text-xs">{{ errors.zipCode }}</p>
          </div>
          <div class="space-y-1.5">
            <label for="organization-city" class="text-sm font-medium">{{
              t('organizations.createDialog.city')
            }}</label>
            <Input
              id="organization-city"
              v-model="city"
              v-bind="cityAttrs"
              autocomplete="address-level2"
            />
            <p v-if="errors.city" class="text-destructive text-xs">{{ errors.city }}</p>
          </div>
          <div class="space-y-1.5">
            <label for="organization-country" class="text-sm font-medium">{{
              t('organizations.createDialog.country')
            }}</label>
            <SearchableSelect
              id="organization-country"
              v-model="country"
              :options="countryOptions"
              :placeholder="t('organizations.settings.company.country')"
              :search-placeholder="t('organizations.settings.company.countrySearch')"
              :empty-text="t('organizations.settings.company.noMatches')"
              :invalid="!!errors.country"
            />
            <p v-if="errors.country" class="text-destructive text-xs">{{ errors.country }}</p>
          </div>
        </div>
      </form>

      <DialogFooter>
        <Button variant="outline" @click="open = false">{{ t('common.cancel') }}</Button>
        <Button
          type="submit"
          form="create-organization-form"
          :disabled="createOrganization.isPending.value"
        >
          {{ t('organizations.createDialog.submit') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
